import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';

import { BUSINESS_DEFAULT } from '../lib/businessInfo';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";

const EFFECTIVE_DATE = 'June 1, 2026';

const SECTIONS = [
  {
    title: '1. Acceptance & Eligibility',
    body: 'By creating an account, booking, or otherwise using the RideBuraq platform (the "Platform"), you agree to be bound by these Terms of Service and our Privacy Policy. You must be at least 18 years old and able to form a binding contract. If you do not agree, do not use the Platform.',
  },
  {
    title: '2. Technology Platform Only — Not a Carrier',
    body: 'RideBuraq operates a technology platform that connects riders with independent, third-party transportation providers ("Drivers"). RideBuraq is NOT a transportation carrier, taxi service, or common carrier, and does NOT provide transportation services. Like other rideshare and booking platforms, we solely facilitate the connection, booking, and payment between riders and Drivers. Any transportation is provided by the Driver, not by RideBuraq.',
  },
  {
    title: '3. Independent Contractors',
    body: 'All Drivers are independent contractors. They are NOT employees, agents, partners, joint venturers, or representatives of RideBuraq. RideBuraq does not direct, supervise, or control Drivers, their vehicles, or the manner, route, timing, or means of any trip. RideBuraq is not responsible or liable for the acts, omissions, conduct, or negligence of any Driver or rider.',
  },
  {
    title: '4. No Guarantee & No Endorsement',
    body: 'While Drivers may be subject to a vetting process, RideBuraq does not guarantee, warrant, or endorse the quality, safety, suitability, punctuality, or legality of any Driver, vehicle, or trip. Background or licensing checks, where performed, are not a guarantee of future conduct. You are responsible for verifying that a ride meets your needs before entering a vehicle.',
  },
  {
    title: '5. Assumption of Risk',
    body: 'You understand and voluntarily accept that using any transportation arranged through the Platform carries inherent risks, including risks of accident, injury, delay, or property loss. To the fullest extent permitted by law, you assume all such risks and agree that RideBuraq is not responsible for any resulting harm.',
  },
  {
    title: '6. Insurance & Licensing',
    body: 'Each Driver is solely responsible for maintaining a valid driver\'s license, vehicle registration, and all insurance and permits required by applicable law. RideBuraq does NOT provide insurance coverage for trips booked through the Platform. Any transportation is provided under the Driver\'s own insurance, licensing, and authority.',
  },
  {
    title: '7. Disclaimer of Warranties',
    body: 'The Platform and all related services are provided "AS IS" and "AS AVAILABLE," without warranties of any kind, whether express, implied, or statutory, including any implied warranties of merchantability, fitness for a particular purpose, non-infringement, availability, or uninterrupted or error-free operation. Some jurisdictions do not allow certain disclaimers, so parts of this section may not apply to you.',
  },
  {
    title: '8. Limitation of Liability',
    body: 'To the maximum extent permitted by law, RideBuraq and its owners, officers, employees, and affiliates will NOT be liable for any indirect, incidental, special, consequential, exemplary, or punitive damages, or for any injury, death, loss, delay, missed connection, property damage, or claim arising out of or relating to any trip, Driver, rider, or use of the Platform — even if advised of the possibility. Your use of the Platform is at your own risk. In all cases, RideBuraq\'s total aggregate liability for any claim is limited to the greater of (a) the booking/service fee RideBuraq actually retained on the specific ride giving rise to the claim, or (b) US $100.',
  },
  {
    title: '9. Indemnification',
    body: 'You agree to defend, indemnify, and hold harmless RideBuraq and its owners, officers, employees, and affiliates from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or connected with your use of the Platform, your trips, your violation of these Terms, or your violation of any law or the rights of any third party.',
  },
  {
    title: '10. Payments, Fees & Cancellations',
    body: 'Rides are paid in advance at the time of booking. Fares are processed securely through our third-party payment processor; RideBuraq does not store your full card details. Prices may include a platform/booking fee. Cancellation fees may apply as disclosed at booking. Except where required by law, payments are non-refundable once a Driver has been assigned or dispatched.',
  },
  {
    title: '11. User Conduct',
    body: 'You agree to provide accurate information, treat Drivers respectfully, comply with all laws, and not misuse the Platform (including fraud, harassment, unlawful activity, or interfering with its operation). RideBuraq may refuse service, suspend, or terminate accounts that violate these Terms or pose a risk to the community.',
  },
  {
    title: '12. Third-Party Services',
    body: 'The Platform relies on third-party services (for example, payment processing and mapping/geolocation). RideBuraq is not responsible for the availability, accuracy, or conduct of those third parties, and your use of them may be subject to their own terms.',
  },
  {
    title: '13. Force Majeure',
    body: 'RideBuraq is not liable for any failure or delay caused by events beyond its reasonable control, including weather, traffic, acts of God, labor disputes, network or power outages, government action, or Driver unavailability.',
  },
  {
    title: '14. Dispute Resolution — Binding Arbitration & Class Action Waiver',
    body: 'To the extent permitted by law, any dispute arising out of or relating to these Terms or the Platform will be resolved by final and binding individual arbitration, and NOT in court, except that either party may bring qualifying claims in small-claims court. YOU AND RIDEBURAQ WAIVE ANY RIGHT TO A JURY TRIAL AND TO PARTICIPATE IN A CLASS, COLLECTIVE, OR REPRESENTATIVE ACTION. You may opt out of arbitration by written notice within 30 days of first accepting these Terms.',
  },
  {
    title: '15. Governing Law',
    body: 'These Terms are governed by the laws of the State of New York, without regard to its conflict-of-laws rules, except that the arbitration provision is governed by the Federal Arbitration Act.',
  },
  {
    title: '16. Changes to These Terms',
    body: 'RideBuraq may update these Terms from time to time. We will update the effective date above, and your continued use of the Platform after changes take effect constitutes acceptance of the revised Terms.',
  },
  {
    title: '17. Termination',
    body: 'You may stop using the Platform and delete your account at any time. RideBuraq may suspend or terminate access at its discretion, including for violations of these Terms. Sections that by their nature should survive termination (including liability, indemnification, and dispute resolution) will survive.',
  },
  {
    title: '18. Severability & Entire Agreement',
    body: 'If any provision of these Terms is found unenforceable, the remaining provisions remain in full effect. These Terms, together with the Privacy Policy, are the entire agreement between you and RideBuraq regarding the Platform.',
  },
  {
    title: '19. Text Messaging (SMS)',
    body: `By providing your mobile number, you consent to receive booking-related and account text messages from ${BUSINESS_DEFAULT.name}. Message and data rates may apply and message frequency varies. Reply STOP to unsubscribe or HELP for help. ${BUSINESS_DEFAULT.name} does not share mobile numbers, text messaging opt-in data, or consent with third parties or affiliates for marketing purposes. See our Privacy Policy for full details.`,
  },
  {
    title: '20. Communications (Messaging & Calls)',
    body: 'The Platform lets riders and Drivers coordinate through in-app messaging and masked phone calls connected via a third-party telephony provider, so personal phone numbers are not shared between them. In-app messages may be stored and reviewed by RideBuraq\'s dispatch team for safety, quality, support, and dispute resolution. You agree to use these features only to coordinate your ride and not for harassment, spam, solicitation, or any unlawful purpose. RideBuraq may suspend access to messaging or calling for misuse.',
  },
  {
    title: '21. Buraq Miles',
    body: 'When a ride is booked for someone else on a business account, the account chooses whether miles go to the booker, the traveler, or the company.',
  },
  {
    title: '22. Contact',
    body: `RideBuraq is operated by ${BUSINESS_DEFAULT.name}, ${BUSINESS_DEFAULT.address}. Questions about these Terms can be sent to support@rideburaq.com.`,
  },
];

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-orange-600/15 blur-[120px]" />

      <div className="relative w-full max-w-3xl mx-auto px-5 lg:px-8 pb-16">
        <header className="flex items-center justify-between py-6 lg:py-8">
          <button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-gray-300 hover:text-white hover:border-orange-500/40 active:scale-95 transition-all"
            aria-label="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="logo-home"
            onClick={() => navigate('/')}
            className="flex items-center gap-2.5 active:scale-95 transition-transform"
            aria-label="Home"
          >
            <img src={LOGO} alt="RideBuraq" className="w-8 h-8 object-contain" />
            <span className="text-[11px] font-semibold tracking-[0.35em] text-gray-400 uppercase">RideBuraq</span>
          </button>
        </header>

        <div className="pt-4 pb-8">
          <p className="text-[11px] tracking-[0.3em] uppercase text-orange-400/80 mb-2">Terms & Legal</p>
          <h1 className="text-4xl lg:text-5xl font-semibold tracking-tight leading-none">Terms of Service</h1>
          <p className="text-xs text-gray-500 mt-3">Effective {EFFECTIVE_DATE}</p>
        </div>

        <div className="rounded-2xl border border-orange-500/20 bg-orange-500/5 p-4 mb-8 flex gap-3">
          <ShieldCheck className="h-5 w-5 text-orange-400 shrink-0 mt-0.5" />
          <p className="text-sm text-gray-300">
            RideBuraq is a technology platform. We connect riders with independent contractor Drivers who
            carry their own insurance — we are not a transportation carrier.
          </p>
        </div>

        <div className="space-y-6">
          {SECTIONS.map((s, i) => (
            <div key={i}>
              <h2 className="text-lg font-semibold text-white mb-2">{s.title}</h2>
              <p className="text-sm text-gray-400 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-600 mt-10">
          These Terms are provided as a general template and do not constitute legal advice. Please have
          them reviewed by a licensed attorney before relying on them.
        </p>
      </div>
    </div>
  );
};

export default Terms;
